import { Controller, useFormContext } from "react-hook-form";
import {
  GeneralAssetType,
  useDropdownBrand,
  useDropdownDocumentType,
  useDropdownGroupAsset,
} from "../../utils";
import {
  Button,
  DateInput,
  Input,
  Modal,
  NumberInput,
  Select,
} from "components";
import moment from "moment";
import { PictureSection } from "../PictureSection";
import { useDropdownVendor, useToggle } from "utils";
import { useContext, useState } from "react";
import { FormDataContext } from "../../context";
import { TbPlus } from "react-icons/tb";
import { CreatePage } from "pages/Card/Transaction/CreateNewVendor/components";

export function FormSectionTabGeneralAsset(props: {
  onSubmit: () => Promise<void>; 
  setGroupCode: (code: string) => void;
}) {
  // context
  const { dataAsset } = useContext(FormDataContext);

  // use-form
  const { control, watch, setValue } = useFormContext<GeneralAssetType>();

  // utils
  const { isActive, toggle } = useToggle(false);

  // hooks
  const { data: dataGroupAsset, isLoading: isLoadingGroupAsset } =
    useDropdownGroupAsset();
  const { data: dataBrand, isLoading: isLoadingBrand } = useDropdownBrand();
  const { data: dataDocument, isLoading: isLoadingDocument } =
    useDropdownDocumentType();
  const {
    data: dataVendor,
    isLoading: isLoadingVendor,
    refetch,
  } = useDropdownVendor();

  // variables
  const brandSelected = dataBrand.filter(
    (item) => item.mr_group_id === Number(watch("mr_group_id"))
  );  

  //handle loading save
  const [loading, setLoading] = useState(false);
  const handleSubmit = async () => {
    try {
      setLoading(true);
      await props.onSubmit(); // tunggu API selesai
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <PictureSection />

      {/* DATE */}
      <div className="mb-2 ">
        <Controller
          control={control}
          name="transaction_date"
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label="TRANSACTION DATE"
              onChange={(date) =>
                field.onChange(moment(date).format("YYYY-MM-DD"))
              }
              selected={moment(field.value).toDate()}
              value={moment(field.value).format("DD/MM/YYYY")}
              error={fieldState.error?.message}
              className="w-full"
            />
          )}
        />
      </div>

      {/* GROUP */}
      <div className="mb-2">
        <Controller
          control={control}
          name="mr_group_id"
          render={({ field, fieldState }) => (
            <Select
              label="GROUP"
              placeholder="Select group..."
              {...field}
              error={fieldState.error?.message}
              onChange={(e) => {
                field.onChange(e?.value);
                const group = dataGroupAsset.find(
                  (item) => item.value === e?.value?.toString()
                );
                props.setGroupCode(group?.group_code ?? "");

                const brandList = dataBrand.filter(
                  (item) => item.mr_group_id === Number(e?.value)
                );
                if (brandList.length > 0) {
                  setValue("mr_brand_id", brandList[0].value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                } else {
                  setValue("mr_brand_id", '');
                }
              }}
              options={dataGroupAsset}
              isLoading={isLoadingGroupAsset}
              value={dataGroupAsset.find(
                (item) => item.value === field.value?.toString()
              )}
            />
          )}
        />
      </div>

      {/* BRAND */}
      <div className="mb-2">
        <Controller
          control={control}
          name="mr_brand_id"
          render={({ field, fieldState }) => (
            <Select
              label="BRAND"
              placeholder="Select brand..."
              {...field}
              error={fieldState.error?.message}
              onChange={(e) => field.onChange(e?.value)}
              isLoading={isLoadingBrand}
              options={brandSelected}
              value={brandSelected.find(
                (item) => item.value === field.value?.toString()
              )}
            />
          )}
        />
      </div>

      {/* CODE */}
      <div className="mb-2">
        <Controller
          control={control}
          name="asset_code"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label="CODE"
              value={field.value}
              error={fieldState.error?.message}
              disabled
            />
          )}
        />
      </div>

      {/* DESCRIPTION */}
      <div className="mb-2">
        <Controller
          control={control}
          name="asset_name"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label="DESCRIPTION"
              value={field.value ?? ""}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      {/* DOCUMENT TYPE */}
      <div className="mb-2">
        <Controller
          control={control}
          name="mr_document_id"
          render={({ field, fieldState }) => (
            <Select
              label="DOCUMENT TYPE"
              placeholder="Select group..."
              {...field}
              error={fieldState.error?.message}
              isLoading={isLoadingDocument}
              onChange={(e) => field.onChange(e?.value)}
              options={dataDocument}
              value={dataDocument.find(
                (item) => item.value === field.value?.toString()
              )}
            />
          )}
        />
      </div>

      {/* SERIAL NUMBER */}
      <div className="mb-2">
        <Controller
          control={control}
          name="serial_document"
          render={({ field, fieldState }) => (
            <Input
              {...field}
              label="SERIAL NUMBER"
              value={field.value ?? ""}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>

      {/* ACQUISITION COST */}
      <div className="mb-2">
        <Controller
          control={control}
          name="acquisition_cost"
          render={({ field, fieldState }) => (
            <NumberInput
              textRight
              label="ACQUISITION COST"
              value={field.value}
              error={fieldState.error?.message}
              onValueChange={(e) => field.onChange(e.floatValue ?? 0)}
            />
          )}
        />
      </div>

      {/* ACQUISITION DATE */}
      <div className="mb-2">
        <Controller
          control={control}
          name="acquisition_date"
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label="ACQUISITION DATE"
              onChange={(date) => {
                field.onChange(moment(date).format("YYYY-MM-DD"));
                setValue(
                  "depreciation_expired",
                  moment(date)
                    .add(Number(watch("depreciation_duration")), "months")
                    .format("YYYY-MM-DD")
                );
              }}
              selected={moment(field.value).toDate()}
              value={moment(field.value).format("DD/MM/YYYY")}
              error={fieldState.error?.message}
              disabled={Boolean(dataAsset?.history?.length! > 0)}
            />
          )}
        />
      </div>

      {/* DEPRECIATION DURATION */}
      <div className="mb-2 flex items-end w-full">
        <div className="flex-grow">
          <Controller
            control={control}
            name="depreciation_duration"
            render={({ field, fieldState }) => (
              <NumberInput
                {...field}
                label="DEPRECIATION DURATION"
                value={field.value}
                error={fieldState.error?.message}
                className="w-full"
                onValueChange={(e) => {
                  field.onChange(e.value);
                  setValue(
                    "depreciation_expired",
                    moment(watch("acquisition_date"))
                      .add(e.value, "months")
                      .format("YYYY-MM-DD")
                  );
                }}
                inputClass="!rounded-r-none"
                decimalScale={0}
                disabled={Boolean(dataAsset?.history?.length! > 0)}
              />
            )}
          />
        </div>
        <div className="w-[100px]">
          <Input
            value={"months"}
            disabled
            inputClass="!rounded-l-none"
            className="mt-[36px] w-full"
          />
        </div>
      </div>

      {/* DEPRECIATION EXPIRED */}
      <div className="mb-2">
        <Controller
          control={control}
          name="depreciation_expired"
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label="END OF DEPRECIATION PERIOD"
              onChange={(date) => {
                field.onChange(moment(date).format("YYYY-MM-DD"));
              }}
              selected={moment(field.value).toDate()}
              value={moment(field.value).format("DD/MM/YYYY")}
              error={fieldState.error?.message}
              readOnly
            />
          )}
        />
      </div>

      {/* VENDOR */}
      <div className="flex items-top">
        <Controller
          control={control}
          name="vendor_id"
          render={({ field, fieldState }) => (
            <Select
              label="VENDOR"
              placeholder="Select vendor..."
              {...field}
              error={fieldState.error?.message}
              onChange={(e) => field.onChange(e?.value)}
              options={dataVendor}
              value={dataVendor.find(
                (item) => item.value === field.value?.toString()
              )}
              isLoading={isLoadingVendor}
              controlClass="!rounded-r-none"
            />
          )}
        />

        <div className="mt-[36px]">
          <Button
            type="button"
            color="primary"
            onClick={toggle}
            className="!rounded-r-lg"
          >
            <TbPlus size={24} />
          </Button>
        </div>
      </div>

      {/* DEPRECIATION EXPIRED */}
      {/* <div className="mb-2">
        <Controller
          control={control}
          name="depreciation_expired"
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label="END OF DEPRECIATION PERIOD"
              onChange={(date) => {
                field.onChange(moment(date).format("YYYY-MM-DD"));
              }}
              selected={moment(field.value).toDate()}
              value={moment(field.value).format("DD/MM/YYYY")}
              error={fieldState.error?.message}
              readOnly
            />
          )}
        />
      </div> */}

      <div className="mt-10 flex justify-end">
          <Button
              type="button"
              color="primary"
              onClick={handleSubmit}
              loading={loading ? "true" : undefined}
          >
              Save
          </Button>
      </div>

      {isActive && <CreateVendor onSuccess={() => refetch()} toggle={toggle} />}
    </section>
  );
}

function CreateVendor(props: { onSuccess: () => void; toggle: () => void }) {
  return (
    <Modal
      isOpen
      title="CREATE NEW VENDOR"
      size="xl"
      closeModal={() => {
        props.toggle();
        props.onSuccess();
      }}
    >
      <Modal.Body>
        <CreatePage asModal />
      </Modal.Body>
    </Modal>
  );
}
